/**
 * Guards against missing values and string-coerced undefined (e.g. `${undefined}` → "undefined").
 */
export function isValidImageSrc(src: unknown): src is string {
	if (typeof src !== "string") return false;

	const trimmed = src.trim();
	if (!trimmed || trimmed === "undefined" || trimmed === "null") return false;
	if (/^undefined(\/|$)/i.test(trimmed)) return false;

	return true;
}

export function getStaticMediaBaseUrl(): string | undefined {
	const base = process.env.NEXT_PUBLIC_STATIC_MEDIA_API_URL?.trim();
	if (!base || base === "undefined") return undefined;
	return base;
}

export function buildMediaUrl(mediaName?: string | null): string | undefined {
	const base = getStaticMediaBaseUrl();
	if (!base || !mediaName) return undefined;
	return base + mediaName;
}

/** Resolve hero poster/image URL on the server for first paint. */
export function getBannerImageFromMedia(
	media?: Array<{
		media_type?: string;
		media_name?: string;
		media_path?: string;
		poster_desktop?: { media_name?: string; media_path?: string };
		poster_mobile?: { media_name?: string; media_path?: string };
		pivot?: { media_device?: string; slug?: string | null };
	}>,
	device: "desktop" | "mobile" = "desktop"
): string | undefined {
	if (!media?.length) return undefined;

	const pick = (targetDevice: "desktop" | "mobile") =>
		media.findLast(
			(item) =>
				item?.pivot?.media_device === targetDevice &&
				item?.pivot?.slug !== "logo"
		);

	const selected = pick(device) ?? pick(device === "desktop" ? "mobile" : "desktop");
	if (!selected?.media_path) return undefined;

	if (selected.media_type === "video") {
		const poster =
			device === "mobile" ? selected.poster_mobile : selected.poster_desktop;
		if (poster?.media_path && poster.media_name) {
			return buildMediaUrl(poster.media_name);
		}
		return undefined;
	}

	return buildMediaUrl(selected.media_name);
}

/** Match custom image loader params for hero preload hints. */
export function buildPreloadImageUrl(
	src: string,
	width = 1920,
	quality = 75
): string {
	if (!isValidImageSrc(src)) return src;

	try {
		const url = new URL(src);
		url.searchParams.set("w", width.toString());
		url.searchParams.set("q", quality.toString());
		url.searchParams.set("ext", "webp");
		return url.toString();
	} catch {
		const separator = src.includes("?") ? "&" : "?";
		return `${src}${separator}w=${width}&q=${quality}&ext=webp`;
	}
}
